import { useState } from "react";
import { createStore, applyMiddleware, Store } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import {
  RootState,
  RootAction,
  VersionedCustomInputsFormState,
} from "@/lib/redux/types";
import { rootReducer, getPaymentFrequencyDefaults } from "@/lib/redux/reducers";
import {
  FormConfig,
  FreeFormAddressElementType,
} from "@evergiving/common/dist/es2015/form";
import {
  PaymentSourceKind,
  Versioned,
  PostalAddressKind,
} from "@evergiving/common/dist/es2015/pledge";
import thunkMiddleware from "redux-thunk";
import { useRouter } from "next/router";
import { useFormRouter } from "../hooks/useFormRouter";

// This file adapted from https://github.com/vercel/next.js/blob/v10.0.3/examples/with-redux/store.js

type FormStore = Store<RootState, RootAction>;

let store: FormStore | undefined;

const initialVersion: Versioned<string | undefined> = {
  value: undefined,
  version: 0,
};

function customInputsInitialFormState(
  formConfig: FormConfig
): VersionedCustomInputsFormState {
  if (formConfig.customInputs !== undefined) {
    return Object.entries(formConfig.customInputs).reduce(
      (acc, [tag, elementConfig], _index) => {
        const key = `customInput_${tag}`;
        if (elementConfig.fieldType === "checkbox") {
          return {
            ...acc,
            ...{ [key]: { value: elementConfig.uncheckedValue, version: 0 } },
          };
        }
        return { ...acc, ...{ [key]: initialVersion } };
      },
      {}
    );
  }
  return {};
}

export function initialStateFromFormConfig(
  formConfig: FormConfig,
  authorizedPaymentSource?: PaymentSourceKind
): Omit<
  RootState,
  "formSourceRef" | "formPledgePaymentAuthorization"
> {
  const defaultPaymentFrequency =
    "payment" in formConfig ? formConfig.payment.defaultFrequency : undefined;

  const defaultPaymentSourceKind =
    "payment" in formConfig
      ? [
          PaymentSourceKind.CreditCard,
          PaymentSourceKind.DirectDebit,
          PaymentSourceKind.StripePayment,
          PaymentSourceKind.ApplePay,
          PaymentSourceKind.GooglePay,
        ]
          .filter(
            (paymentSourceKind) =>
              authorizedPaymentSource === undefined ||
              authorizedPaymentSource === paymentSourceKind
          )
          .map((paymentSourceKind) => ({
            paymentSourceKind,
            index: Object.keys(formConfig.payment).indexOf(paymentSourceKind),
          }))
          .filter(({ index }) => index >= 0)
          .sort((a, b) => a.index - b.index)[0]
      : undefined;

  return {
    firstName: initialVersion,
    lastName: initialVersion,
    phoneNumber: initialVersion,
    emailAddress: initialVersion,
    cardHolder: initialVersion,
    cardNumber: initialVersion,
    cardExpiry: initialVersion,
    cardVerificationCode: initialVersion,
    directDebitAccountHolder: initialVersion,
    directDebitAccountNumber: initialVersion,
    directDebitFinancialInstitution: { value: undefined, version: 0 },
    directDebitAccountType: initialVersion,
    directDebitSortCode: initialVersion,
    directDebitIBAN: initialVersion,
    dateOfBirth: initialVersion,
    gender: { value: undefined, version: 0 },
    belfioreCode: initialVersion,
    personalIdentificationNumber: initialVersion,
    profession: initialVersion,
    title: initialVersion,
    idTypeSighted: initialVersion,

    [FreeFormAddressElementType.Element3]: initialVersion,
    [FreeFormAddressElementType.Element4]: initialVersion,
    [FreeFormAddressElementType.Element5]: initialVersion,
    [FreeFormAddressElementType.Element6]: initialVersion,
    [FreeFormAddressElementType.Element7]: initialVersion,
    [FreeFormAddressElementType.Element8]: initialVersion,
    [FreeFormAddressElementType.Element9]: initialVersion,
    [FreeFormAddressElementType.Element10]: initialVersion,
    [FreeFormAddressElementType.Element11]: initialVersion,
    [FreeFormAddressElementType.Element12]: initialVersion,
    [FreeFormAddressElementType.Element13]: initialVersion,
    [FreeFormAddressElementType.Element14]: initialVersion,
    [FreeFormAddressElementType.Element15]: initialVersion,

    /**
     * @deprecated Prefer customInputs instead.
     */
    custom1: initialVersion,
    ...customInputsInitialFormState(formConfig),
    customInputs: [],

    giftAidConsent: false,
    termsAndConditionsConsent: false,
    phoneContactChecked: false,
    smsContactChecked: false,
    emailContactChecked: false,
    postContactChecked: false,
    whatsappContactChecked: false,

    emailAppraisal: undefined,
    ibanAppraisal: undefined,
    testingReference: undefined,
    previousSubmissionState: undefined,

    errors: {},
    countryCode:
      "country" in formConfig
        ? initialVersion
        : { value: formConfig.defaultCountryCode, version: 1 },
    paymentFrequency: defaultPaymentFrequency,
    ...getPaymentFrequencyDefaults(
      formConfig,
      defaultPaymentFrequency,
      defaultPaymentSourceKind?.paymentSourceKind
    ),
    paymentSourceKind: defaultPaymentSourceKind?.paymentSourceKind,
    paymentDay: undefined,

    ...(formConfig.address &&
    !("melissa" in formConfig.address) &&
    !("loqate" in formConfig.address)
      ? {
          postalAddress: {
            kind: PostalAddressKind.FreeForm,
            element2: formConfig.defaultCountryCode,
            element3: undefined,
            element4: undefined,
            element5: undefined,
            element6: undefined,
            element7: undefined,
            element8: undefined,
            element9: undefined,
            element10: undefined,
            element11: undefined,
            element12: undefined,
            element13: undefined,
            element14: undefined,
            element15: undefined,
          },
        }
      : {}),
  };
}

function initStore(preloadedState: RootState): FormStore {
  return createStore(
    rootReducer,
    preloadedState,
    composeWithDevTools(applyMiddleware(thunkMiddleware))
  );
}

export const initializeStore = (preloadedState: RootState): FormStore => {
  let newStore = store ?? initStore(preloadedState);

  // After navigating to a page with an initial Redux state, merge that state
  // with the current state in the store, and create a new store
  if (preloadedState && store) {
    newStore = initStore({
      ...store.getState(),
      ...preloadedState,
    });
    // Reset the current store
    store = undefined;
  }

  // For SSG and SSR always create a new store
  if (typeof window === "undefined") return newStore;
  // Create the store once in the client
  if (!store) store = newStore;

  return newStore;
};

export function useStore(formConfig: FormConfig): FormStore {
  const { formLinkFromQueryParam } = useFormRouter();
  const {
    query: { i: instance },
  } = useRouter();

  const formLink = formLinkFromQueryParam(instance);
  const resolved = formLink.kind === "resolved" ? formLink : undefined;

  const [persistentStore] = useState<FormStore>(
    initializeStore({
      ...initialStateFromFormConfig(
        formConfig,
        resolved?.authorization?.authorization.paymentSource
      ),
      formSourceRef: resolved?.ref,
      formPledgePaymentAuthorization: resolved?.authorization,
    })
  );

  return persistentStore;
}
